import {
  Box,
  Container,
  Divider,
  Link,
  Paper,
  Stack,
  Typography,
} from "@mui/material";

export interface LegalSection {
  title: string;
  paragraphs?: string[];
  bullets?: string[];
}

interface LegalDocumentPageProps {
  title: string;
  lastUpdatedLabel: string;
  lastUpdatedValue: string;
  intro: string[];
  sections: LegalSection[];
  contactTitle: string;
  contactDescription: string;
  websiteLabel: string;
  websiteUrl: string;
  phoneLabel: string;
  phoneValue: string;
}

const LegalDocumentPage = ({
  title,
  lastUpdatedLabel,
  lastUpdatedValue,
  intro,
  sections,
  contactTitle,
  contactDescription,
  websiteLabel,
  websiteUrl,
  phoneLabel,
  phoneValue,
}: LegalDocumentPageProps) => {
  return (
    <Container maxWidth="md" sx={{ py: { xs: 4, md: 6 } }}>
      <Paper elevation={1} sx={{ p: { xs: 3, md: 5 }, borderRadius: 3 }}>
        <Stack spacing={3}>
          <Box>
            <Typography variant="h3" component="h1" sx={{ fontWeight: 700 }}>
              {title}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {lastUpdatedLabel}: {lastUpdatedValue}
            </Typography>
          </Box>

          <Stack spacing={2}>
            {intro.map((paragraph) => (
              <Typography key={paragraph} variant="body1" color="text.primary">
                {paragraph}
              </Typography>
            ))}
          </Stack>

          <Divider />

          <Stack spacing={4}>
            {sections.map((section) => (
              <Stack key={section.title} spacing={1.5}>
                <Typography variant="h5" component="h2" sx={{ fontWeight: 600 }}>
                  {section.title}
                </Typography>
                {section.paragraphs?.map((paragraph) => (
                  <Typography key={paragraph} variant="body1" color="text.primary">
                    {paragraph}
                  </Typography>
                ))}
                {section.bullets?.length ? (
                  <Box component="ul" sx={{ m: 0, pl: 3 }}>
                    {section.bullets.map((bullet) => (
                      <Box component="li" key={bullet} sx={{ mb: 1 }}>
                        <Typography variant="body1" color="text.primary">
                          {bullet}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                ) : null}
              </Stack>
            ))}
          </Stack>

          <Divider />

          <Stack spacing={1.5}>
            <Typography variant="h5" component="h2" sx={{ fontWeight: 600 }}>
              {contactTitle}
            </Typography>
            <Typography variant="body1">{contactDescription}</Typography>
            <Typography variant="body1">
              {websiteLabel}:{" "}
              <Link href={websiteUrl} target="_blank" rel="noreferrer">
                {websiteUrl}
              </Link>
            </Typography>
            <Typography variant="body1">
              {phoneLabel}: {phoneValue}
            </Typography>
          </Stack>
        </Stack>
      </Paper>
    </Container>
  );
};

export default LegalDocumentPage;
